import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useLang, type Key } from '../i18n'
import { api, isApiError, type BidJob, type SlideImportJob, type SlideImportMode } from '../lib/api'
import { bidJobsKey, jobKindWords, jobNoteWords, jobStatusText, jobSubject } from '../lib/bid-jobs'
import { useBidProfile } from '../lib/bid-profile'
import { importOffers, inFlight, jobUnreadable, refusalWords, setPendingReport, type Words } from '../lib/slide-import'
import { ImportPanelButtons } from './ImportPanelButtons'
import { IndexResultBody } from './IndexResultModal'
import { KeptTemplateLine } from './KeptTemplateLine'

/** How often the details read a job still running. */
const POLL_MS = 2000

const MODE_WORDS: Record<SlideImportMode, Key> = {
  template: 'pptx_mode_template',
  source: 'pptx_mode_source',
  pictures: 'pptx_mode_pictures',
}

const fill = (s: string, words: Pick<Words, 'vars'>): string =>
  Object.entries(words.vars ?? {}).reduce((acc, [k, v]) => acc.replaceAll(`{${k}}`, () => String(v)), s)

/**
 * A job's details (AB#462), opened by its row's link in the bid home's Jobs section. Each kind reads
 * its own record, through the read it already has.
 *
 * - **An import**: deck, mode, member, times, slides written, status, error; keeping the source
 *   formatting, what became of the masters with a link to the template (AB#483). "Import report" and
 *   "Compare with original" close the dialog and open the part on the import's first slide with that
 *   panel; Retry or Retry renders for a member with write rights. Whoever may see the job may open
 *   both: a member who has since become a viewer gets them read-only, without Retry.
 * - **An indexing run**: what the "Indexation result" modal shows (IndexResultModal.tsx).
 *
 * Focus moves in on open, is trapped here, Escape closes, and focus returns to the row's link.
 */
export function JobDetailsDialog({
  bid,
  job,
  onOpenPart,
  onClose,
}: {
  bid: string
  job: BidJob
  onOpenPart: (room: string) => void
  onClose: () => void
}) {
  const { t, lang } = useLang()
  const uid = useId()
  const dialogRef = useRef<HTMLDivElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null
    closeRef.current?.focus()
    return () => prev?.focus()
  }, [])

  /** Escape closes; Tab / Shift+Tab stay inside the dialog. */
  const onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.stopPropagation()
      onClose()
      return
    }
    if (e.key !== 'Tab' || !dialogRef.current) return
    const focusable = [...dialogRef.current.querySelectorAll<HTMLElement>('a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])')].filter(
      (el) => !(el as HTMLButtonElement).disabled,
    )
    if (focusable.length === 0) return
    const first = focusable[0]!
    const last = focusable[focusable.length - 1]!
    // The event's target, not document.activeElement: at the end of <body>, the Tabster of the Feedback
    // widget's Fluent UI has already moved focus to its own guard by the time this runs.
    if (e.shiftKey && (e.target === first || e.target === dialogRef.current)) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && e.target === last) {
      e.preventDefault()
      first.focus()
    }
  }

  const when = (iso: string | undefined): string =>
    iso ? new Date(iso).toLocaleString(lang === 'fr' ? 'fr-FR' : 'en-US', { dateStyle: 'medium', timeStyle: 'short' }) : '—'
  const status = jobStatusText(job)
  const subject = jobSubject(job)
  const statusText = (
    <>
      {fill(t(status.key), status)}
      {job.note && <span className="jobs-note">{t(jobNoteWords(job.note))}</span>}
    </>
  )
  const member = job.launchedBy.displayName ?? t('jobs_member_unknown')

  return createPortal(
    <div className="modal-backdrop" onMouseDown={onClose}>
      <div
        ref={dialogRef}
        className="modal job-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${uid}-title`}
        tabIndex={-1}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
      >
        <div className="modal-head">
          <h2 id={`${uid}-title`}>{t(jobKindWords(job.kind))}</h2>
          <button ref={closeRef} type="button" className="modal-close" aria-label={t('close')} onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="modal-body job-body">
          {job.kind === 'pptx_import' ? (
            <ImportDetails bid={bid} job={job} member={member} statusText={statusText} when={when} onOpenPart={onOpenPart} onClose={onClose} />
          ) : (
            <IndexDetails
              bid={bid}
              job={job}
              subject={subject === null || typeof subject === 'string' ? subject : fill(t(subject.key), subject)}
              member={member}
              statusText={statusText}
              when={when}
            />
          )}
        </div>
      </div>
    </div>,
    document.body,
  )
}

/** One label and its value in a job's details. */
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </>
  )
}

function ImportDetails({
  bid,
  job,
  member,
  statusText,
  when,
  onOpenPart,
  onClose,
}: {
  bid: string
  job: BidJob
  member: string
  statusText: ReactNode
  when: (iso: string | undefined) => string
  onOpenPart: (room: string) => void
  onClose: () => void
}) {
  const { t } = useLang()
  const queryClient = useQueryClient()
  const { canEdit } = useBidProfile(bid)
  const queryKey = ['slide-import', job.id] as const
  const read = useQuery({
    queryKey,
    queryFn: () => api.slideImport(job.id),
    retry: false,
    refetchInterval: (q) => (q.state.data && inFlight(q.state.data.status) ? POLL_MS : false),
  })
  const imp: SlideImportJob | undefined = read.data
  const offers = imp ? importOffers(imp, Date.now()) : null
  const waiting = imp?.cutOutsPending ?? 0

  const [retrying, setRetrying] = useState(false)
  const [refusal, setRefusal] = useState<string | null>(null)
  const refreshed = (): void => {
    void queryClient.invalidateQueries({ queryKey })
    void queryClient.invalidateQueries({ queryKey: bidJobsKey(bid) })
  }
  const retry = (): void => {
    if (retrying) return
    setRetrying(true)
    setRefusal(null)
    api.retrySlideImport(job.id).then(
      () => {
        setRetrying(false)
        refreshed()
      },
      (e: unknown) => {
        setRetrying(false)
        // Another tab was first: what it did is what the read shows.
        if (isApiError(e) && (e.code === 'already_imported' || e.code === 'import_running')) return refreshed()
        const words = refusalWords(e, 'retry')
        setRefusal(words ? fill(t(words.key), words) : isApiError(e) && e.serverMessage ? e.serverMessage : t('prompt_failed'))
      },
    )
  }

  return (
    <>
      <dl className="job-fields">
        <Field label={t('jobs_field_deck')}>{job.subject.name}</Field>
        {imp && <Field label={t('jobs_field_mode')}>{t(MODE_WORDS[imp.mode])}</Field>}
        <Field label={t('jobs_col_member')}>{member}</Field>
        <Field label={t('jobs_field_started')}>{when(job.startedAt)}</Field>
        <Field label={t('jobs_field_ended')}>{when(job.finishedAt)}</Field>
        {imp && <Field label={t('jobs_field_slides')}>{imp.slides.length}</Field>}
        <Field label={t('jobs_col_status')}>{statusText}</Field>
      </dl>
      {imp?.template && <KeptTemplateLine bid={bid} template={imp.template} onOpen={onClose} />}
      {!imp && !read.isFetched && <p className="pptx-note">{t('loading')}</p>}
      {!imp && read.isError && (
        <p className="prompt-error" role="alert">
          {t(jobUnreadable(read.error) ? 'pptx_job_lost' : 'prompt_failed')}
        </p>
      )}
      {imp?.error && (
        <p className="prompt-error" role="alert">
          {imp.error}
        </p>
      )}
      {imp && waiting > 0 && !inFlight(imp.status) && <p className="pptx-note">{fill(t('sl_import_report_waiting'), { vars: { n: waiting } })}</p>}
      {imp && (
        <ImportPanelButtons
          job={imp}
          onOpen={(panel) => {
            setPendingReport(imp.room, imp.id, panel)
            onClose()
            onOpenPart(imp.room)
          }}
        />
      )}
      {refusal && (
        <p className="prompt-error" role="alert">
          {refusal}
        </p>
      )}
      <div className="prompt-acts">
        <button type="button" className="mini ghost" onClick={onClose}>
          {t('close')}
        </button>
        {offers?.retry && canEdit && (
          <button type="button" className="mini ok prompt-confirm" onClick={retry} disabled={retrying}>
            {retrying && <span className="brand-spinner" aria-hidden="true" />}
            {t(offers.retry)}
          </button>
        )}
      </div>
    </>
  )
}

function IndexDetails({
  bid,
  job,
  subject,
  member,
  statusText,
  when,
}: {
  bid: string
  job: BidJob
  subject: string | null
  member: string
  statusText: ReactNode
  when: (iso: string | undefined) => string
}) {
  const { t } = useLang()
  const read = useQuery({
    queryKey: ['sp-index-job', bid, job.id],
    queryFn: () => api.spIndexJob(bid, job.id),
    retry: false,
    refetchInterval: (q) => (q.state.data?.status === 'running' ? POLL_MS : false),
  })
  // Still walking its selection: the result's "Nothing was processed" would be a verdict too early.
  const nothingYet = read.data?.status === 'running' && read.data.progress.length === 0
  return (
    <>
      <dl className="job-fields">
        {subject && <Field label={t('jobs_field_files')}>{subject}</Field>}
        <Field label={t('jobs_col_member')}>{member}</Field>
        <Field label={t('jobs_field_started')}>{when(job.startedAt)}</Field>
        <Field label={t('jobs_field_ended')}>{when(job.finishedAt)}</Field>
        <Field label={t('jobs_col_status')}>{statusText}</Field>
      </dl>
      {!nothingYet && <h3 className="job-section">{t('bidsite_index_result_link')}</h3>}
      {nothingYet ? null : read.data ? (
        <IndexResultBody job={read.data} />
      ) : read.isError ? (
        <p className="prompt-error" role="alert">
          {t(jobUnreadable(read.error) ? 'pptx_job_lost' : 'prompt_failed')}
        </p>
      ) : (
        <p className="pptx-note">{t('loading')}</p>
      )}
    </>
  )
}
